import type { StoreApi } from "zustand";

const sessionStoreResetFunctions = new Set<() => void>();

const SESSION_OWNED_STORAGE_KEY_PREFIX = "session-owned:";

/**
 * Returns the sessionStorage key for state that belongs to the signed-in session. Keys built here
 * are removed when the session ends, even when the app that owns them has not loaded in this tab.
 */
export function sessionOwnedStorageKey(name: string) {
  return `${SESSION_OWNED_STORAGE_KEY_PREFIX}${name}`;
}

/**
 * Registers a Zustand store for reset when the authenticated session ends.
 * Use only for state that must not carry between users or companies.
 */
export function registerSessionStore<T>(store: StoreApi<T>) {
  sessionStoreResetFunctions.add(() => {
    store.setState(store.getInitialState(), true);
  });
}

export function resetSessionStores() {
  sessionStoreResetFunctions.forEach((resetStore) => resetStore());

  // A lazily loaded app registers its stores only after it loads, so its persisted session state is
  // also removed by key. The registered resets above have already cleared the in-memory copies.
  const sessionOwnedKeys: string[] = [];

  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);

    if (key?.startsWith(SESSION_OWNED_STORAGE_KEY_PREFIX)) {
      sessionOwnedKeys.push(key);
    }
  }

  sessionOwnedKeys.forEach((key) => sessionStorage.removeItem(key));
}
